import { useState } from 'react';
import { FileDown, FileText, Download, Maximize2 } from 'lucide-react';
import type { Block } from '../../../types/parcours';

interface PdfBlock extends Block {
  type: 'pdf';
  url?: string;
  title?: string;
  /** Hauteur du viewer (px ou CSS, défaut : 70vh) */
  height?: number | string;
}

interface Props {
  block: PdfBlock;
}

/**
 * Renderer PDF avec liseuse intégrée (iframe natif navigateur, supporte
 * Chrome / Safari / Firefox / Edge sans dépendance externe). Bouton
 * "Télécharger" toujours visible. Bouton "Plein écran" qui ouvre le PDF
 * dans un nouvel onglet.
 */
export default function PdfBlock({ block }: Props) {
  const [loaded, setLoaded] = useState(false);

  if (!block.url) return null;

  const title = block.title ?? 'Document PDF';
  const height = block.height ?? '70vh';

  return (
    <div className="my-6 rounded-2xl ring-1 ring-neutral-200 overflow-hidden bg-white shadow-sm">
      {/* Header : titre + actions */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-neutral-50 border-b border-neutral-200">
        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-[#d03484]/10">
          <FileText className="w-4 h-4 text-[#d03484]" />
        </div>
        <p className="flex-1 min-w-0 font-semibold text-sm text-[#30475e] truncate">
          {title}
        </p>
        <a
          href={block.url}
          target="_blank"
          rel="noopener noreferrer"
          title="Ouvrir en plein écran"
          className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-lg text-neutral-500 hover:text-[#d03484] hover:bg-white transition-colors"
        >
          <Maximize2 className="w-4 h-4" />
        </a>
        <a
          href={block.url}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#d03484] to-[#d47254] px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:shadow-md transition-all whitespace-nowrap"
        >
          <Download className="w-3.5 h-3.5" />
          Télécharger
        </a>
      </div>

      {/* Viewer */}
      {!loaded ? (
        // État "preview" : on attend le clic utilisateur pour éviter de
        // charger le PDF systématiquement (perf + bande passante)
        <button
          type="button"
          onClick={() => setLoaded(true)}
          className="w-full flex flex-col items-center justify-center gap-4 py-16 bg-gradient-to-br from-[#fbfaf8] to-white hover:from-[#fde7f2] hover:to-[#fde2d3] transition-colors group"
          style={{ height }}
        >
          <FileDown className="w-12 h-12 text-neutral-400 group-hover:text-[#d03484] transition-colors" />
          <span className="text-sm font-semibold text-[#30475e]">
            Cliquez pour ouvrir le document
          </span>
          <span className="text-xs text-neutral-500">
            Le PDF s'affichera dans une liseuse intégrée
          </span>
        </button>
      ) : (
        <iframe
          src={`${block.url}#view=FitH`}
          title={title}
          className="block w-full bg-neutral-100"
          style={{ height, border: 0 }}
          loading="lazy"
        />
      )}
    </div>
  );
}
